/**
 * <s-favorite-button> — heart toggle that saves a product handle as a favorite in this browser.
 * Favorites live in localStorage under "yeezo:favorites"; <s-favorites-list> reads the same key.
 *
 * Attributes: id (command target), data-handle, data-label-add, data-label-remove
 * Commands:   --toggle-favorite — add or remove this product from favorites
 * Events:     dispatches `favorites:change` on document with { favorites } after each change
 */
const STORAGE_KEY = 'yeezo:favorites';

function readFavorites() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function writeFavorites(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {}
  document.dispatchEvent(new CustomEvent('favorites:change', { detail: { favorites: list } }));
}

class SFavoriteButton extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('command', (event) => {
      if (event.command === '--toggle-favorite') this.#toggle();
    }, opts);
    document.addEventListener('favorites:change', () => this.#render(), opts);
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY) this.#render();
    }, opts);
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #toggle() {
    const handle = this.dataset.handle;
    if (!handle) return;
    const list = readFavorites();
    const index = list.indexOf(handle);
    if (index === -1) list.unshift(handle);
    else list.splice(index, 1);
    writeFavorites(list);
  }

  #render() {
    const button = this.querySelector('button');
    if (!button) return;
    const active = readFavorites().includes(this.dataset.handle);
    const label = active ? this.dataset.labelRemove : this.dataset.labelAdd;
    button.setAttribute('aria-pressed', String(active));
    if (label) {
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);
    }
  }
}

if (!customElements.get('s-favorite-button')) customElements.define('s-favorite-button', SFavoriteButton);
